import { forwardRef, useImperativeHandle, useRef } from 'react'
import { Heading, TypographyV2 as Typography } from '@which/seatbelt'

import { Boxed } from '../../components/Boxed'
import { ErrorMessage } from '../../components/ErrorMessage'
import { NextButton } from '../../components/NextButton'
import { RadioGroup } from '../../components/RadioGroup'
import { TextArea } from '../../components/TextArea'
import globalStyles from '../../styles/GlobalStyles.module.scss'
import type { SurveyModuleProps } from '../../types/toolTypes'
import styles from './SurveyModule.module.scss'

export const SurveyModule = forwardRef(
  (
    {
      radioQuestions,
      handleRadioChange,
      stateValue,
      handleSubmit,
      title,
      standFirst,
      textareaQuestions,
      handleTextareaChange,
      textareaState,
      errorCount,
      errorMessage = 'Something went wrong, please try again',
      nextButtonText = 'Send feedback',
    }: SurveyModuleProps,
    ref
  ) => {
    const placeHolderRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => ({
      placeHolderRef: placeHolderRef.current,
    }))

    const showError = () => {
      if (errorCount && errorCount > 0) {
        return (
          <span className="sb-text-align-center">
            <ErrorMessage errorText={errorMessage} />
          </span>
        )
      }
    }

    return (
      <div className={globalStyles.maxWidth} ref={placeHolderRef}>
        {title && <Heading heading={title} headingTag="h2" headingType="medium" />}
        <div className={styles.surveyModuleWrapper}>
          <Typography
            className={styles.surveyModuleStandFirst}
            textStyle="sb-text-heading-standfirst"
          >
            {standFirst}
          </Typography>
          {radioQuestions.map((radioQuestion) => {
            const { buttons, id, radiosStandFirst, title: radioTitle } = radioQuestion
            const isSubmitValid = stateValue.find((val) => val.title === radioTitle)?.isValid
            return (
              <Boxed className={styles.surveyModuleQuestion} key={radioTitle}>
                <div className={styles.surveyModuleQuestionTitle}>
                  <Heading heading={radioTitle} headingTag="h3" headingType="small" />
                  {radiosStandFirst && (
                    <Typography textStyle="sb-text-body-default-regular">
                      {radiosStandFirst}
                    </Typography>
                  )}
                </div>
                <RadioGroup
                  buttons={buttons}
                  handleChange={handleRadioChange}
                  id={id}
                  isSubmitValid={isSubmitValid}
                  stateValue={stateValue}
                  title={radioTitle}
                />
              </Boxed>
            )
          })}
          {textareaQuestions &&
            handleTextareaChange &&
            textareaQuestions.map((textareaQuestion) => {
              const {
                title: textAreaTitle,
                defaultValue,
                placeholderText,
                id,
                isOptional,
              } = textareaQuestion
              const isSubmitValid = textareaState?.find((val) => val.id === id)?.isValid
              return (
                <Boxed className={styles.surveyModuleQuestion} key={textAreaTitle}>
                  <Typography
                    className={styles.surveyModuleQuestionTitle}
                    tag="h3"
                    textStyle="sb-text-heading-small"
                  >
                    {textAreaTitle}
                    {isOptional && <span> (optional)</span>}
                  </Typography>
                  <TextArea
                    className={styles.surveyModuleTextarea}
                    defaultValue={defaultValue}
                    handleChange={handleTextareaChange}
                    handleSubmit={handleSubmit}
                    id={id}
                    isFocused={false}
                    isSubmitValid={isSubmitValid}
                    maxChars={2500}
                    optional={isOptional}
                    placeholderText={placeholderText}
                    srLabel={textAreaTitle}
                  />
                </Boxed>
              )
            })}
          <div className={styles.surveyModuleSubmitButton}>
            <NextButton buttonText={nextButtonText} onClick={handleSubmit} />
          </div>
          {showError()}
        </div>
      </div>
    )
  }
)
